import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { type OutlineEntry } from "../editor/latex";
import type { CompileResult, LogEntry } from "../lib/api";

// ---------------------------------------------------------------- outline

export function Outline({ entries, currentLine, onJump }: { entries: OutlineEntry[]; currentLine: number; onJump: (line: number) => void }) {
  const minLevel = Math.min(...entries.map((e) => e.level), 9);
  let current = -1;
  entries.forEach((e, i) => {
    if (e.line <= currentLine) current = i;
  });
  if (entries.length === 0) return <div className="review-empty">No sections in this file.</div>;
  return (
    <div className="outline">
      {entries.map((e, i) => (
        <button
          key={`${e.line}-${i}`}
          className={`outline-item${i === current ? " current" : ""}`}
          style={{ paddingLeft: 12 + (e.level - minLevel) * 12, fontWeight: e.level === minLevel ? 600 : undefined }}
          onClick={() => onJump(e.line)}
          title={`${e.kind}: line ${e.line}`}
        >
          {e.title || <i className="muted">untitled</i>}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- compile log

export function LogPanel({
  result,
  rawLog,
  onJump,
  onClose,
}: {
  result: CompileResult | null;
  rawLog: string | null;
  onJump: (entry: LogEntry) => void;
  onClose: () => void;
}) {
  const [filter, setFilter] = useState<"all" | "error" | "warning" | "typesetting">("all");
  const [showRaw, setShowRaw] = useState(false);
  const back = (
    <button className="btn btn-sm" onClick={onClose} title="Close the log and show the PDF">
      <ArrowLeft size={14} /> Back to PDF
    </button>
  );
  if (!result)
    return (
      <div className="log-view">
        <div className="log-summary">{back}</div>
        <div className="review-empty">Compile to see the log.</div>
      </div>
    );
  const entries = result.entries.filter((e) => filter === "all" || e.level === filter);
  return (
    <div className="log-view">
      <div className="log-summary">
        {back}
        <span className="log-sep" />
        <button className={`btn btn-sm${filter === "all" ? " btn-primary" : ""}`} onClick={() => setFilter("all")}>
          All
        </button>
        <button className="btn btn-sm" onClick={() => setFilter("error")}>
          Errors <span className="badge badge-error">{result.counts.errors}</span>
        </button>
        <button className="btn btn-sm" onClick={() => setFilter("warning")}>
          Warnings <span className="badge badge-warning">{result.counts.warnings}</span>
        </button>
        <button className="btn btn-sm" onClick={() => setFilter("typesetting")}>
          Boxes <span className="badge">{result.counts.typesetting}</span>
        </button>
        <span className="grow" />
        <button className="btn btn-sm btn-ghost" onClick={() => setShowRaw(!showRaw)}>
          {showRaw ? "Hide raw log" : "Raw log"}
        </button>
      </div>
      {result.message && <div className="log-banner">{result.message}</div>}
      {result.status === "failure" && !result.message && (
        <div className="log-banner">No PDF was produced. Fix the first error below and recompile.</div>
      )}
      {entries.map((e, i) => (
        <div key={i} className={`log-entry ${e.level}`}>
          {e.file && (
            <button className="loc" onClick={() => onJump(e)}>
              {e.file}
              {e.line ? `:${e.line}` : ""}
            </button>
          )}
          <div className="message">{e.message}</div>
          {e.content && <pre>{e.content}</pre>}
        </div>
      ))}
      {entries.length === 0 && !result.message && <div className="review-empty">Nothing here. 🎉</div>}
      {showRaw && <pre className="raw-log">{rawLog ?? "Loading…"}</pre>}
    </div>
  );
}
